import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';
import type { ServiceTaskSourceValue, ServiceTaskStatusValue } from '@/utils/constants';

// ─── DTOs (mirror server/src/services/service.service.ts) ────────────────────

export interface ServiceTaskListItem {
  id: number;
  taskCode: string;
  leadId: number;
  clientName: string;
  arn: string;
  productCode: string;
  productName: string;
  source: ServiceTaskSourceValue;
  cycleNumber: number | null;
  status: ServiceTaskStatusValue;
  assignedToId: number | null;
  assignedToName: string | null;
  dueDate: string | null;
  progress: number;
  createdAt: string;
}

export interface ServiceMetrics {
  total: number;
  notPickedUp: number;
  inProgress: number;
  onHold: number;
  blocked: number;
  completedMtd: number;
  avgTatDays: number | null;
}

export interface ServiceTaskDetail extends ServiceTaskListItem {
  sourceId: number;
  notes: string | null;
  completedAt: string | null;
  completedById: number | null;
  updatedAt: string;
}

export interface ListServiceTasksParams {
  search?: string;
  status?: ServiceTaskStatusValue;
  source?: ServiceTaskSourceValue;
  sortBy?: 'dueDate' | 'createdAt' | 'clientName' | 'status';
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface UpdateServiceTaskInput {
  status?: Exclude<ServiceTaskStatusValue, 'Completed'>;
  progress?: number;
  notes?: string;
  assignedToId?: number | null;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const fetchServiceTasks = async (
  params: ListServiceTasksParams,
): Promise<{
  items: ServiceTaskListItem[];
  total: number;
  totalPages: number;
  currentPage: number;
}> => {
  const res = await apiClient.get<ApiResponse<ServiceTaskListItem[]>>('/service', { params });
  return {
    items: res.data.data ?? [],
    total: res.data.meta?.total ?? 0,
    totalPages: res.data.meta?.totalPages ?? 1,
    currentPage: res.data.meta?.currentPage ?? 1,
  };
};

export const fetchServiceMetrics = async (): Promise<ServiceMetrics> => {
  const res = await apiClient.get<ApiResponse<ServiceMetrics>>('/service/metrics');
  return res.data.data as ServiceMetrics;
};

export const fetchServiceTaskDetail = async (id: number): Promise<ServiceTaskDetail> => {
  const res = await apiClient.get<ApiResponse<ServiceTaskDetail>>(`/service/${id}`);
  return res.data.data as ServiceTaskDetail;
};

export const patchServiceTask = async (
  id: number,
  input: UpdateServiceTaskInput,
): Promise<ServiceTaskDetail> => {
  const res = await apiClient.patch<ApiResponse<ServiceTaskDetail>>(`/service/${id}`, input);
  return res.data.data as ServiceTaskDetail;
};

export const completeServiceTask = async (id: number): Promise<ServiceTaskDetail> => {
  const res = await apiClient.post<ApiResponse<ServiceTaskDetail>>(`/service/${id}/complete`);
  return res.data.data as ServiceTaskDetail;
};
